import axios, { ResponseType } from 'axios';

import { Logger } from '../Logging';

import { getAxiosInstance } from './axios';
import HttpError from './HttpError';
import NetworkError from './NetworkError';
import parseAxiosError from './parseAxiosError';

/**
 * An HTTP interface using our axios setup for the react-query
 * client generated by orval from our OpenAPI schemas
 */
export const HTTP = async <T>({
  url,
  method,
  params,
  data,
  signal,
  headers,
  isRoverUrl = true,
  maxRedirects = 0,
  responseType,
}: {
  method: 'get' | 'post' | 'put' | 'delete' | 'patch';
  url: string;
  headers?: Record<string, string>;
  params?: Record<string, string | number | number[] | boolean | string[]>; // url params
  data?: unknown; // more strictly typing this is tricky.  Thankfully the various orval function interfaces guard us from incorrect data
  responseType?: ResponseType; // this was in the orval docs, but doesn't seem to be used in our generated client?
  signal?: AbortSignal; // used by react-query to cancel requests
  isRoverUrl?: boolean; // whether to add Rover-specific modifications to requests
  maxRedirects?: number; // the maximum number of redirects to follow
}): Promise<T> => {
  try {
    const ai = getAxiosInstance(); // get our custom axios instance, works on all platforms

    // add the WEBAPP_ROOT_URL to Rover urls when it is defined (SSR Server-Side)
    // must use exactly "process.env.WEBAPP_ROOT_URL" here so the Webpack DefinePlugin can provide it to Django/React apps
    const webappRootUrl = process.env.WEBAPP_ROOT_URL;
    const urlRoot = isRoverUrl && webappRootUrl ? webappRootUrl.replace(/\/$/, '') : ''; // strip the trailing slash if there is one

    const res = await ai({
      url: `${urlRoot}${url}`,
      method,
      params,
      data,
      signal,
      headers: {
        ...headers,
        // for Rover urls, get camelCase responses from the django backend
        ...(isRoverUrl ? { Vary: '*', Accept: 'application/vnd.rover.api.camel+json' } : {}),
      },
      maxRedirects,
      responseType, // default to json response type
    });
    return res.data; // react-query requires us to resolve the data

    // axios throws on non-200 responses
  } catch (e) {
    // cancelled requests can rethrow without special handling
    if (e instanceof axios.Cancel) {
      Logger.debug(`Cancelled request for ${url}`);
      throw e;
    }

    const parsedError = parseAxiosError(e);
    Logger.info(parsedError.message); // Don't report noisy network errors to Sentry, just log them instead.
    throw parsedError; // react-query requires erroring Query Functions to throw
  }
};

// Make the ErrorType in the generated client match what parseAxiosError returns
// Our schema doesn't define error types yet, so we ignore the parameter
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type ErrorType<Error> = HttpError | NetworkError;
